import * as React from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowLeftRight, ArrowRight, GripVertical, Inbox } from 'lucide-react';
import { useContexto } from '@/hooks/useContexto';
import { useParamUrl } from '@/hooks/useUrlEstado';
import { useNegativos } from '@/hooks/negativos';
import { useMovimentos, type AbaMovimentos, type Movimento } from '@/hooks/movimentos';
import { dataHora, numero } from '@/lib/utils';
import { AbasMovimentos } from '@/componentes/layout/AbasMovimentos';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { CabecalhoPagina, Esqueleto } from '@/componentes/ui/primitivos';
import { LogMovimentacoes } from '@/componentes/auditoria/LogMovimentacoes';

function ehAba(valor: string): valor is AbaMovimentos {
  return valor === 'pendentes' || valor === 'hoje' || valor === 'historico';
}

/**
 * Caixa de entrada de trabalho, não relatório: abre no que precisa de ação.
 *
 * A aba PENDENTES ainda não tem API (não há rota /api/movimentos): a tela está
 * inteira e os hooks prontos, e a lista renderiza vazia de verdade, sem mock.
 *
 * HOJE e HISTÓRICO, essas sim, têm dado — e não é o mesmo dado.
 * ------------------------------------------------------------
 * Elas mostram o log de auditoria do WMS, que é a única trilha de autoria que
 * existe: `Usp_ValidaTransferenciaLocalizador` recebe o parâmetro `@CRACHA` e
 * NUNCA o grava, então o PBS sabe que a posição mudou e não sabe por quem.
 * Login, IP e horário exato só existem em `WMS.AUDITORIA`.
 *
 * Antes isso só aparecia em Kardex, e as duas abas aqui — literalmente
 * chamadas "Hoje" e "Histórico" — renderizavam vazias. Quem procurava o
 * histórico de uma movimentação procurava exatamente onde não estava.
 */
export function Movimentos(): JSX.Element {
  const { centro } = useContexto();
  const [abaUrl] = useParamUrl('aba', 'pendentes');
  const aba: AbaMovimentos = ehAba(abaUrl) ? abaUrl : 'pendentes';
  const consulta = useMovimentos(centro, aba);
  const negativos = useNegativos(centro);

  return (
    <div className="space-y-4 p-3 sm:p-5">
      <CabecalhoPagina
        icone={<ArrowLeftRight className="h-5 w-5" />}
        sobrelinha={`Centro ${centro ?? '—'}`}
        titulo="O que precisa da sua ação"
        descricao="Fila de movimentações do centro. A ordem da lista é a prioridade de execução — arraste para reordenar."
      />

      <AbasMovimentos ativa={aba} totalNegativos={negativos.data?.total} />

      {aba === 'hoje' || aba === 'historico' ? (
        <div className="flex min-h-[420px] flex-col">
          <LogMovimentacoes centro={centro} diasPadrao={aba === 'hoje' ? '1' : '30'} />
        </div>
      ) : consulta.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Esqueleto key={i} className="h-16" />
          ))}
        </div>
      ) : consulta.isError ? (
        <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      ) : (
        <FilaMovimentos movimentos={consulta.data ?? []} aba={aba} />
      )}
    </div>
  );
}

const VAZIO: Record<AbaMovimentos, { titulo: string; detalhe: string }> = {
  pendentes: {
    titulo: 'Nenhuma movimentação pendente',
    detalhe: 'Quando houver transferência aguardando confirmação, ela aparece aqui.',
  },
  hoje: {
    titulo: 'Nenhuma movimentação hoje',
    detalhe: 'Movimentações confirmadas no dia aparecem nesta aba.',
  },
  historico: {
    titulo: 'Sem histórico de movimentação',
    detalhe: 'O histórico das transferências confirmadas aparece aqui.',
  },
};

/**
 * A ordem da fila é prioridade de execução, por isso é arrastável (dnd-kit).
 * A ordenação vive só no cliente até existir um campo de prioridade na API.
 */
function FilaMovimentos({
  movimentos,
  aba,
}: {
  movimentos: readonly Movimento[];
  aba: AbaMovimentos;
}): JSX.Element {
  const [ordem, definirOrdem] = React.useState<number[]>(() => movimentos.map((m) => m.id));

  React.useEffect(() => {
    definirOrdem(movimentos.map((m) => m.id));
  }, [movimentos]);

  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (movimentos.length === 0) {
    const vazio = VAZIO[aba];
    return (
      <EstadoVazio titulo={vazio.titulo} detalhe={vazio.detalhe} icone={<Inbox className="h-8 w-8" />} />
    );
  }

  const aoTerminarArraste = (evento: DragEndEvent): void => {
    const { active, over } = evento;
    if (!over || active.id === over.id) return;
    definirOrdem((atual) => {
      const de = atual.indexOf(Number(active.id));
      const para = atual.indexOf(Number(over.id));
      return de < 0 || para < 0 ? atual : arrayMove(atual, de, para);
    });
  };

  const ordenados = ordem
    .map((id) => movimentos.find((m) => m.id === id))
    .filter((m): m is Movimento => m !== undefined);

  return (
    <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={aoTerminarArraste}>
      <SortableContext items={ordem} strategy={verticalListSortingStrategy}>
        <ul className="space-y-2">
          {ordenados.map((movimento) => (
            <LinhaMovimento key={movimento.id} movimento={movimento} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function LinhaMovimento({ movimento }: { movimento: Movimento }): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: movimento.id,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`cartao flex items-center gap-3 p-3.5 transition-shadow ${
        isDragging ? 'opacity-70 shadow-forte' : 'hover:shadow-media'
      }`}
    >
      <button
        type="button"
        aria-label="Reordenar"
        className="flex min-h-toque min-w-toque cursor-grab items-center justify-center rounded-lg text-textoSuave transition-colors hover:bg-superficie2 hover:text-texto active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">
          <span className="font-mono font-semibold">{movimento.produto}</span>{' '}
          <span className="text-textoFraco">{movimento.produtoDescricao}</span>
        </p>
        <p className="flex flex-wrap items-center gap-1.5 text-xs text-textoFraco">
          <span className="rounded-md bg-superficie2 px-1.5 py-0.5 font-mono font-semibold">
            {movimento.origemEndereco}
          </span>
          <ArrowRight className="h-3 w-3 text-primaria" />
          <span className="rounded-md bg-primaria-suave px-1.5 py-0.5 font-mono font-semibold text-primaria">
            {movimento.destinoEndereco}
          </span>
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="tabular text-[15px] font-bold">{numero(movimento.quantidade)}</p>
        <p className="text-xs text-textoFraco">{dataHora(movimento.criadoEm)}</p>
      </div>
    </li>
  );
}
